/**
 * This file is a migration of the Authentication code scaffolded using .NET Core Web Application for SPA (React)
 * with Individual Authentication.
 *
 * See here: https://docs.microsoft.com/en-us/dotnet/core/tools/dotnet-new#spa
 */
import { ApplicationPaths, LogoutActions, QueryParameterNames } from './AuthorizationConstants';
import { completeSignOut, isAuthenticated, signOut } from './AuthorizeService';
import { anonymize } from '../utils/analytics';

async function logout(returnUrl): Promise<void> {
  const state = { returnUrl };
  const authenticated = await isAuthenticated();

  if (authenticated) {
    const result = await signOut(state);
    switch (result.status) {
      case 'redirect':
        break;
      case 'success':
        anonymize();
        await navigateToReturnUrl(returnUrl);
        break;
      case 'fail':
        return Promise.reject(result.message);
      default:
        throw new Error('Invalid authentication result status.');
    }
  } else {
    // User is already logged out, navigate to the return url
    await navigateToReturnUrl(returnUrl);
  }
  return Promise.resolve();
}

async function processLogoutCallback(): Promise<void> {
  const url = window.location.href;
  const result = await completeSignOut(url);
  switch (result.status) {
    case 'redirect':
      // There should not be any redirects as the only time completeAuthentication finishes
      // is when we are doing a redirect sign in flow.
      throw new Error('Should not redirect.');
    case 'success':
      await navigateToReturnUrl(getReturnUrl(result.state));
      break;
    case 'fail':
      return Promise.reject(result.message);
    default:
      throw new Error('Invalid authentication result status.');
  }
  return Promise.resolve();
}

function getReturnUrl(state?: { returnUrl: string }): string {
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get(QueryParameterNames.ReturnUrl);
  if (fromQuery && !fromQuery.startsWith(`${window.location.origin}/`)) {
    // This is an extra check to prevent open redirects.
    throw new Error(
      'Invalid return url. The return url needs to have the same origin as the current page.'
    );
  }
  return (
    (state && state.returnUrl) ||
    fromQuery ||
    `${window.location.origin}${ApplicationPaths.LoggedOut}`
  );
}

async function navigateToReturnUrl(returnUrl: string): Promise<void> {
  return window.location.replace(returnUrl);
}

export default async function handleLogoutAction(action: LogoutActions): Promise<void> {
  switch (action) {
    case 'logout':
      return logout(getReturnUrl());
    case 'logout-api':
      return logout(window.location);
    case 'logout-callback':
      return processLogoutCallback();
    case 'logged-out':
      break;
    default:
      throw new Error(`Invalid action '${action}'`);
  }
  return Promise.resolve();
}
